import { styled } from "styled-components";
import { Container } from "../ui/Containers";
import { useState } from "react";
import PageTransition from "../../utils/TransitionPage";
import { useLocation, useParams } from "react-router-dom";
import InfoProjectForm from "../ui/Forms/InfoProjectForm";
import Gallery from "../ui/ProjectGallery";
import Links from "../ui/ProjectLinks";
import { formatDateToMonthYear } from "../../utils/helperFunctions";
import { ProjectDataProps } from "../common/types";
import { useLoader } from "../../hooks/useLoader";
import { useAuth } from "../../context/AuthContext";
import { useFetchProjectById } from "../../hooks/useFetchProject";

const ProjectViewContainer = styled.div`
  // border: 1px solid black;
  margin-top: 1.5rem;
  @media (max-width: 576px) {
    margin-top: 5rem;
  }
  display: flex;
  flex-direction: column;
  gap: 40px;

  .titleFont {
    font-size: 25px;
    font-weight: 700;
  }
  .yearFont {
    font-size: 18px;
    font-weight: 600;
  }
  .smallFont {
    font-size: 13px;
    font-weight: 700;
  }
  .flexAlign {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 30px;
  }

  .sectionTitle {
    font-size: 20px;
    font-weight: 600;
    margin-top: 20px;
  }
  .sectionSubTitle {
    font-size: 18px;
    font-weight: 500;
    margin-top: 15px;
  }
  .sectionContent {
    font-size: 16px;
    margin-bottom: 10px;
    span {
      font-weight: 500;
    }
  }
`;

const NoProjectFallBack = styled.div<{ $isProjectPreview: boolean }>`
  text-align: center;
  font-size: 17px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  @media (max-width: 767px) {
    height: ${({ $isProjectPreview }) =>
      $isProjectPreview ? "100vh" : "30vh"};
  }

  @media (min-width: 768px) {
    border: ${({ $isProjectPreview, theme }) =>
      !$isProjectPreview ? `1px solid ${theme.text}` : "none"};
  }
`;

type PreviewProps = {
  project?: ProjectDataProps;
  // mode: 'view' | 'edit'; // 'view' = preview mode from a card | 'edit' = inline preview during add/edit
};

const ProjectView: React.FC<PreviewProps> = ({ project }) => {
  // const [showTransition, setShowTransition] = useState(true);
  const { showLoader, setShowLoader, LoaderComponent } = useLoader(); //passed to projectform
  const { showAlert } = useAuth(); //global alert box, //passed to projectform
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const previewPath = `/project-view/${projectId}`;
  const isProjectView = location.pathname.includes(previewPath);
  const passedName = location.state?.name;

  const projectFromState = project; // safely extract project data from live state
  const {
    showLoader: isLoading,
    project: fetchedProject,
    isFetched,
    LoaderComponent: ProjectLoader,
  } = useFetchProjectById(projectId || "");
  const projectData = projectFromState || (projectId ? fetchedProject : null);

  const [hasTransitionFinished, setHasTransitionFinished] = useState(false);
  // Only show loader after transition finishes AND project is still loading
  const showProjectLoader = hasTransitionFinished && isLoading;
  // Only show fallback message after fetching completes AND no project data
  const showFallback = hasTransitionFinished && isFetched && !projectData;

  //undefined or null checks for the data
  const {
    name,
    isOngoing,
    dateStarted,
    endDate,
    type,
    description,
    galleryData = { imgData: [], vidData: [] },
    links = { liveDemo: "", github: "" },
  } = projectData || {};

  // Check conditions for rendering
  const hasGalleryContent =
    galleryData.imgData.length > 0 || galleryData.vidData.length > 0;
  const hasValidLinks =
    links.liveDemo.trim() !== "" || links.github.trim() !== "";

  const hasValidData = () => {
    if (!projectData) return false;
    // Destructure fields from projectData
    const {
      name,
      isOngoing,
      dateStarted,
      endDate,
      type,
      description,
      galleryData,
      links,
    } = projectData;
    // Check each field for validity
    return (
      (name && name.trim() !== "") ||
      isOngoing ||
      dateStarted ||
      (endDate !== null && endDate !== undefined) ||
      (type?.value && type.value.trim() !== "") ||
      (description && description.trim() !== "") ||
      galleryData?.imgData?.length > 0 ||
      galleryData?.vidData?.length > 0 ||
      links?.liveDemo?.trim() !== "" ||
      links?.github?.trim() !== ""
    );
  };

  const ThePreview = hasValidData() ? (
    <Container>
      <ProjectViewContainer className={`isProjectView ? "App-col" : ""`}>
        <div>
          {name && <p className="titleFont containTextInParagraphs">{name}</p>}
          <p className="yearFont">
            {dateStarted
              ? formatDateToMonthYear(dateStarted)
              : "Start Date Not Set"}{" "}
            -{" "}
            {endDate === 0 && isOngoing
              ? "Present"
              : endDate
              ? formatDateToMonthYear(endDate)
              : "End Date Not Set"}
          </p>
        </div>

        {/* Render description only if available */}
        {description && (
          <div className="sectionContent">
            <div className="flexAlign">
              <p className="containTextInParagraphs">{description}</p>
            </div>
          </div>
        )}

        {hasGalleryContent && (
          <Gallery
            imgData={galleryData.imgData}
            vidData={galleryData.vidData}
          />
        )}

        {type && type !== null && hasValidLinks && (
          <Links
            liveDemo={links.liveDemo}
            github={links.github}
            projectType={type.value}
          />
        )}
        {project && type === null && hasValidLinks && (
          <p>Provide project type to preview link button</p>
        )}

        {name && (
          <InfoProjectForm
            projectName={name}
            showAlert={showAlert}
            showLoader={setShowLoader}
          />
        )}

        {/* Loader displayed during form submission, provide height in preview from user client that is */}
        {showLoader && LoaderComponent}
      </ProjectViewContainer>
    </Container>
  ) : (
    <NoProjectFallBack $isProjectPreview={isProjectView}>
      <div className="center">
        <p>
          {isProjectView && showFallback
            ? "Project data not found"
            : "No project data available. Kindly add a project to see the preview"}
        </p>
      </div>
    </NoProjectFallBack>
  );

  return (
    <>
      <div className="App-col">
        {" "}
        {isProjectView ? (
          <div>
            {!hasTransitionFinished && (
              <PageTransition
                onFinish={() => setHasTransitionFinished(true)}
                passedTitle={passedName ?? "Project"}
                id={projectId ?? ""}
              /> 
            )}

            {showProjectLoader && ProjectLoader}

            {hasTransitionFinished && !showProjectLoader && ThePreview}
          </div>
        ) : (
          // During editing/adding a projec
          <div>{ThePreview}</div>
        )}
      </div>
    </>
  );
};

export default ProjectView;
